import React, { useCallback, useMemo, useState } from 'react';
import {
  useGetAddressesQuery,
  useUpdateOrderMutation,
} from '../../apis/mlkApi/mlk.api.hooks';
import Layout, { OrderRequestResult } from '../../components/Layout/Layout';
import { useBack } from '../../hooks/useBack';
import { useLanguage } from '../../locale/useLanguage';
import { AddressElement } from './components/AddressElement/AddressElement/AddressElement';

import './ChooseFavoriteAddressView.css';
import { Button } from '../../components/Button/Button';
import { useHistory } from 'react-router-dom';
import { Routes } from '../../routes/types';
import { parseRes } from '../../apis/utils';
import { useOrder } from '../../hooks/useOrder';
import { WeekDay } from '../../models';
import dayjs from 'dayjs';

type ChooseFavoriteAddressViewProps = {};

export const ChooseFavoriteAddressView: React.FC<
  ChooseFavoriteAddressViewProps
> = () => {
  const [t] = useLanguage();
  const history = useHistory();
  const goBack = useBack();
  const [selectedAddressId, setSelectedAddressId] = useState<number>();
  const [updateAddress] = useUpdateOrderMutation();
  const [orderId, setOrderId] = useState<number>();
  const order = useOrder();

  const { data: addressesData } = useGetAddressesQuery(undefined);

  const selectedAddress = useMemo(
    () => addressesData?.data?.find((a) => selectedAddressId === a.id),
    [addressesData, selectedAddressId]
  );
  let selectedAddressValid = !!selectedAddress;
  if (
    selectedAddressValid &&
    selectedAddress?.reception &&
    order?.receptionRules
  ) {
    let earliestReceptionTime = Object.keys(selectedAddress.reception).reduce(
      (acc: string | null, key) => {
        const day = selectedAddress.reception![key as WeekDay];
        if (day?.[0]?.from) {
          if (acc === null || parseInt(acc, 10) < parseInt(day[0].from, 10)) {
            return day[0].from;
          }
        }
        return acc;
      },
      null
    );
    let latestReceptionTime = Object.keys(selectedAddress.reception).reduce(
      (acc: string | null, key) => {
        const day = selectedAddress.reception![key as WeekDay];
        if (day?.[1]?.to) {
          if (acc === null || parseInt(acc, 10) > parseInt(day[1].to, 10)) {
            return day[1].to;
          }
        }
        return acc;
      },
      null
    );
    if (
      !earliestReceptionTime ||
      !latestReceptionTime ||
      dayjs(order.receptionRules.startReceptionTime, 'HH:mm').isAfter(
        dayjs(earliestReceptionTime, 'HH:mm')
      ) ||
      dayjs(order.receptionRules.endReceptionTime, 'HH:mm').isBefore(
        dayjs(latestReceptionTime, 'HH:mm')
      )
    ) {
      selectedAddressValid = false;
    }
    const shortestRangeHours = Object.keys(selectedAddress.reception).reduce(
      (acc: number | null, key) => {
        const day = selectedAddress.reception![key as WeekDay];
        if (day?.[0]?.from && day?.[0]?.to && day?.[1]?.from && day?.[1]?.to) {
          const minHours = Math.min(
            parseInt(day[0].to, 10) - parseInt(day[0].from, 10),
            parseInt(day[1].to, 10) - parseInt(day[1].from, 10)
          );
          if (!acc || acc > minHours) {
            return minHours;
          }
        }
        return acc;
      },
      null
    );
    if (
      order.receptionRules.minRangeWidthPerDay &&
      (!shortestRangeHours ||
        shortestRangeHours <
          dayjs.duration(order.receptionRules.minRangeWidthPerDay).asHours())
    ) {
      selectedAddressValid = false;
    }
    const totalWeeklyHours = Object.keys(selectedAddress.reception).reduce(
      (acc: number, key) => {
        const day = selectedAddress.reception![key as WeekDay];
        if (day?.[0]?.from && day?.[0]?.to && day?.[1]?.from && day?.[1]?.to) {
          const hours =
            parseInt(day[0].to, 10) -
            parseInt(day[0].from, 10) +
            parseInt(day[1].to, 10) -
            parseInt(day[1].from, 10);
          return acc + hours;
        }
        return acc;
      },
      0
    );
    if (
      order.receptionRules.minRangesWidthPerWeek &&
      totalWeeklyHours <
        dayjs.duration(order.receptionRules.minRangesWidthPerWeek).asHours()
    ) {
      selectedAddressValid = false;
    }
    if (order.receptionRules?.availableWeekDays) {
      Object.keys(selectedAddress.reception).forEach((day) => {
        if (selectedAddress.reception![day as WeekDay]!.length > 0) {
          let dayIndex = {
            [WeekDay.Mon]: 1,
            [WeekDay.Tue]: 2,
            [WeekDay.Wed]: 3,
            [WeekDay.Thu]: 4,
            [WeekDay.Fri]: 5,
            [WeekDay.Sat]: 6,
            [WeekDay.Sun]: 7,
          };
          if (
            !order.receptionRules?.availableWeekDays.includes(
              dayIndex[day as WeekDay]
            )
          ) {
            selectedAddressValid = false;
          }
        }
      });
    }
    if (order.receptionRules.allowedOrderStatuses) {
      if (
        !order.receptionRules.allowedOrderStatuses.includes(order.mlkStatus)
      ) {
        selectedAddressValid = false;
      }
    }
  }

  const editDeliveryAddress = useCallback(() => {
    if (selectedAddress && orderId) {
      updateAddress({
        orderId: `${orderId}`,
        address: {
          address: selectedAddress.address,
          lat: selectedAddress.lat,
          lon: selectedAddress.lon,
          number: selectedAddress.number,
          route: selectedAddress.route,
          zipCode: selectedAddress.zipCode,
          city: selectedAddress.city,
          country: selectedAddress.country,
          doorbellLatitude: selectedAddress.doorbellLatitude,
          doorbellLongitude: selectedAddress.doorbellLongitude,
        },
        reception: selectedAddress.reception
          ? selectedAddress.reception
          : undefined,
        instructions: {
          floorNumber: selectedAddress.floorNumber
            ? selectedAddress.floorNumber
            : undefined,
          instructions: selectedAddress.instructions ?? '',
          doorbell: selectedAddress.doorbell,
          elevator: selectedAddress.elevator,
        },
      }).then((res) => {
        const { successRes, errorRes } = parseRes(res);
        if (successRes.data) {
          history.replace(Routes.Home);
        }
        if (errorRes.error) {
          // TODO: Handle error
          console.log(errorRes.error);
        }
      });
    }
  }, [addressesData, history, orderId, selectedAddressId, updateAddress]);

  const goToNewAddress = useCallback(() => {
    history.push(Routes.CreateAddress, { isFromChangeAddress: true });
  }, [history]);

  const onOrderUpdate = useCallback((orderResult: OrderRequestResult) => {
    setOrderId(orderResult.data?.id);
  }, []);

  return (
    <Layout
      hasBack
      hasViewportHeight
      onBackClick={goBack}
      onOrderChange={onOrderUpdate}
      title={t('pageTitles.addresses')}
    >
      <div className="choose-favorite-address--list">
        {addressesData &&
          addressesData.data.map((a) => {
            return (
              <AddressElement
                addressId={a.id}
                isSelected={a.id === selectedAddressId}
                onClick={() => {
                  setSelectedAddressId(a.id);
                }}
                addressFirstPart={`${a.route} ${a.number}`}
                addressName={a.name}
                addressType={a.addressType}
                addressSecondPart={`${a.zipCode} ${a.city}`}
                key={a.id}
              />
            );
          })}
      </div>
      <div className="choose-favorite-address--buttons">
        {selectedAddressId && !selectedAddressValid && (
          <p style={{ color: 'var(--danger-color)', marginBottom: '10px' }}>
            {t('chooseFavoriteAddress.incompatibleReception')}
          </p>
        )}
        <Button
          isDisabled={selectedAddressId === undefined || !selectedAddressValid}
          label={t('chooseFavoriteAddress.selectAddress')}
          onClick={editDeliveryAddress}
          isPrimary
        />
        <div className="choose-favorite-address--last-button">
          <Button
            onClick={goToNewAddress}
            label={t('chooseFavoriteAddress.addNew')}
          />
        </div>
      </div>
    </Layout>
  );
};
